// Library methods
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Styles
import { noHoverBackgroundSx } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Utils
import { getVisualField } from "src/utils/exams/examReportsHelper";
import { getLocalDate } from "src/utils/date/dateHelper";
import {
  getExamStats,
  PROGRESSION_STAT_KEYS,
} from "src/utils/exams/progressionAnalysisHelper";

// Types
import {
  ProgressionExamSummaryType,
  SelectedProgressionExamType,
} from "src/services/exams/types";

type BaselineExamSummaryType = {
  exam: ProgressionExamSummaryType;
  examDetail?: SelectedProgressionExamType;
  isSelected?: boolean;
  isDisabled?: boolean;
  isLoadingDetail?: boolean;
  onSelect?: () => void;
};

const BaselineExamSummary = ({
  exam,
  examDetail,
  isSelected,
  isDisabled,
  isLoadingDetail,
  onSelect,
}: BaselineExamSummaryType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const examReport = examDetail?.reportData;

  const completionDate = useMemo(
    () =>
      exam?.completionDate
        ? getLocalDate(exam.completionDate, clinicInfo?.timezone)
        : "N/A",
    [exam?.completionDate, clinicInfo?.timezone]
  );

  const columns = useMemo(
    () => [
      { title: t("Eye"), value: exam?.eye ? t(exam.eye) : "N/A" },
      {
        title: t("Date"),
        value: completionDate,
      },
    ],
    [exam?.eye, completionDate, t]
  );

  const stats = useMemo(() => {
    const examStats = getExamStats(examReport);
    return PROGRESSION_STAT_KEYS.map((key) => ({
      title: t(key),
      value: examStats[key],
    }));
  }, [examReport, t]);

  return (
    <Grid container border="1px solid #000" borderRadius="4px">
      <Grid
        item
        xs={12}
        display="flex"
        alignItems="center"
        sx={{
          borderBottom: "1px solid #000000",
          padding: "4px 8px",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {onSelect && (
            <Checkbox
              checked={Boolean(isSelected)}
              disabled={isDisabled}
              onChange={onSelect}
              inputProps={{ "aria-label": t("Use as Baseline") }}
              sx={{
                padding: 0,
                boxShadow: "none",
                "&.MuiButtonBase-root.MuiCheckbox-root": {
                  color: "#E2772E",
                },
                ...noHoverBackgroundSx,
              }}
            />
          )}
          <Typography fontSize="14px" fontWeight={600}>
            {exam?.algorithm ? t(exam.algorithm) : "N/A"} |{" "}
            {getVisualField(exam?.gridType) || "N/A"}
          </Typography>
        </Box>

        <Typography
          fontSize="12px"
          fontWeight={600}
          color="#E2772E"
          border="1px solid #E2772E"
          borderRadius="4px"
          px="4px"
        >
          {t("RetinaLogik")}
        </Typography>
      </Grid>

      <Grid xs={12} item container sx={{ padding: "4px 8px" }}>
        <Grid item xs={12} container rowGap="4px">
          {columns.map((column) => (
            <Grid item xs={4} key={column.title}>
              <Typography fontSize="12px">
                <span style={{ fontWeight: 600 }}>{column.title}:</span>{" "}
                {column.value}
              </Typography>
            </Grid>
          ))}
          {examReport ? (
            stats.map((stat) => (
              <Grid item xs={4} key={stat.title}>
                {stat.value !== null && (
                  <Typography fontSize="12px">
                    <span style={{ fontWeight: 600 }}>{stat.title}: </span>
                    {stat.value}
                  </Typography>
                )}
              </Grid>
            ))
          ) : isLoadingDetail ? (
            <Grid item xs={12}>
              <Typography fontSize="12px">{t("Loading...")}</Typography>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default BaselineExamSummary;
